import type { FormChatEntry, FormField } from "@ark-bot/protocol";
import { useState } from "react";

import { useText } from "../i18n.js";
import { FORM_CARD } from "../text/chat.js";

interface Props {
  readonly entry: FormChatEntry;
  readonly onSubmit: (formId: string, values: Record<string, string>) => Promise<void>;
  readonly onCancel: (formId: string) => Promise<void>;
}

type Words = (typeof FORM_CARD)["en"];

/** What the user typed, shown back on the card once it has been sent. */
function answerOf(field: FormField, values: Readonly<Record<string, string>> | undefined, t: Words): string {
  const value = values?.[field.id];
  if (value == null || value.length === 0) return "—";
  return field.type === "checkbox" ? (value === "true" ? t.yes : t.no) : value;
}

function Input({ field, value, onChange }: { readonly field: FormField; readonly value: string; readonly onChange: (value: string) => void }): React.JSX.Element {
  const t = useText(FORM_CARD);
  if (field.type === "textarea") return <textarea rows={3} value={value} required={field.required === true} placeholder={field.placeholder} onChange={(event) => onChange(event.target.value)} />;
  if (field.type === "checkbox") return <input type="checkbox" checked={value === "true"} onChange={(event) => onChange(String(event.target.checked))} />;
  if (field.type === "select") {
    return (
      <select value={value} required={field.required === true} onChange={(event) => onChange(event.target.value)}>
        <option value="">{t.choose}</option>
        {(field.options ?? []).map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }
  return <input type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"} value={value} required={field.required === true} placeholder={field.placeholder} onChange={(event) => onChange(event.target.value)} />;
}

/**
 * The agent asking several things at once (ARCHITECTURE §6.12). The turn is
 * waiting on this card, so it stays in the conversation after it is answered,
 * showing what was said rather than an empty form.
 */
export function FormCard({ entry, onSubmit, onCancel }: Props): React.JSX.Element {
  const t = useText(FORM_CARD);
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(entry.fields.map((field) => [field.id, field.type === "checkbox" ? "false" : ""])));
  const [busy, setBusy] = useState(false);
  const waiting = entry.status === "waiting";

  const send = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await onSubmit(entry.formId, values);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`bubble-row form form-${entry.status}`}>
      <form className="form-card" onSubmit={(event) => void send(event)}>
        <div className="handoff-head">
          <span className="handoff-label">{entry.title}</span>
          <span className="handoff-status">{t[entry.status]}</span>
        </div>
        {entry.note == null ? null : <div className="form-note">{entry.note}</div>}
        {entry.fields.map((field) => (
          <label key={field.id} className={field.type === "checkbox" ? "form-field inline" : "form-field"}>
            <span>
              {field.label}
              {field.required === true && waiting ? <span className="form-required"> *</span> : null}
            </span>
            {waiting ? <Input field={field} value={values[field.id] ?? ""} onChange={(value) => setValues((current) => ({ ...current, [field.id]: value }))} /> : <span className="form-answer">{answerOf(field, entry.values, t)}</span>}
          </label>
        ))}
        {waiting ? (
          <div className="handoff-actions">
            <button type="submit" className="primary" disabled={busy}>
              {t.send}
            </button>
            <button type="button" disabled={busy} onClick={() => void onCancel(entry.formId)} title={t.dismissAbout}>
              {t.dismiss}
            </button>
          </div>
        ) : null}
      </form>
    </div>
  );
}
